// src/context/BusinessContext.tsx
// Contexto global del "Negocio activo" — la mayoría de vistas de
// publicación (Calendario, Publicar, ComposePost, Aprobar) se
// comportan en función de un Business seleccionado por el usuario.
//
// Persistimos el ID en localStorage para que sobreviva reloads y
// re-seleccionamos la lista periódicamente para reflejar creaciones
// desde otras pestañas.

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef,
  useState, type ReactNode,
} from 'react';

import { listBusinesses, type Business } from '@/lib/api_businesses';

const STORAGE_KEY = 'active_business_id';

export interface BusinessContextValue {
  /** All active businesses the user can operate on. Null = not loaded yet. */
  businesses: Business[] | null;
  loading: boolean;
  /** Currently selected business — null when none available or not chosen. */
  active: Business | null;
  /** ID accessor for convenience. */
  activeId: string | null;
  setActive: (id: string | null) => void;
  /** Force refresh of the list (e.g. after creating a new business). */
  refresh: () => Promise<void>;
}

const Ctx = createContext<BusinessContextValue>({
  businesses: null,
  loading: true,
  active: null,
  activeId: null,
  setActive: () => { /* noop */ },
  refresh: async () => { /* noop */ },
});

export function BusinessProvider({ children }: { children: ReactNode }) {
  const [businesses, setBusinesses] = useState<Business[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveIdState] = useState<string | null>(() => {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
  });
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listBusinesses(false);
      if (!mounted.current) return;
      setBusinesses(rows);
      // Auto-pick first if nothing chosen or the stored id is gone.
      setActiveIdState((prev) => {
        if (prev && rows.some((b) => b.id === prev)) return prev;
        const first = rows[0]?.id ?? null;
        try {
          if (first) localStorage.setItem(STORAGE_KEY, first);
          else localStorage.removeItem(STORAGE_KEY);
        } catch { /* ignore */ }
        return first;
      });
    } catch {
      // Silent — rendering code handles `businesses=null`.
      if (mounted.current) setBusinesses([]);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    // Only fetch after auth — Businesses API is admin-scoped.
    const token = localStorage.getItem('auth_token');
    if (token) refresh();
    else setLoading(false);
    return () => { mounted.current = false; };
  }, [refresh]);

  const setActive = useCallback((id: string | null) => {
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* quota / private mode */ }
    setActiveIdState(id);
  }, []);

  const value = useMemo<BusinessContextValue>(() => ({
    businesses,
    loading,
    active: businesses?.find((b) => b.id === activeId) ?? null,
    activeId,
    setActive,
    refresh,
  }), [businesses, loading, activeId, setActive, refresh]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useActiveBusiness() {
  return useContext(Ctx);
}
